import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Videos from "../../utils/Videos";
import { heroSlides } from "../../data/heroSlides";

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRefs = useRef([]);
  const preloadRefs = useRef([]);

  // Video arrays for mobile and web
  const mobileVideos = useMemo(
    () => [
      Videos.hero_mobile_one,
      Videos.hero_mobile_two,
      Videos.hero_mobile_three,
      Videos.hero_mobile_four,
      Videos.hero_mobile_five,
    ],
    []
  );

  const webVideos = useMemo(
    () => [
      Videos.hero_web_one,
      Videos.hero_web_two,
      Videos.hero_web_three,
      Videos.hero_web_four,
      Videos.hero_web_five,
    ],
    []
  );

  // Get current video based on screen size
  const getCurrentVideo = () => {
    const isMobile = window.innerWidth < 768;
    const videos = isMobile ? mobileVideos : webVideos;
    return videos[currentSlide % videos.length];
  };

  // Get all videos for current screen size
  const getAllVideos = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    return isMobile ? mobileVideos : webVideos;
  }, [mobileVideos, webVideos]);

  // Preload videos for smooth transitions
  useEffect(() => {
    const videos = getAllVideos();
    videos.forEach((videoSrc, index) => {
      if (!preloadRefs.current[index]) {
        preloadRefs.current[index] = document.createElement("video");
        preloadRefs.current[index].src = videoSrc;
        preloadRefs.current[index].preload = "auto";
        preloadRefs.current[index].muted = true;
        preloadRefs.current[index].loop = true;
      }
    });
  }, [getAllVideos]);

  // Handle video transitions
  useEffect(() => {
    if (!isAutoPlaying || isHovered) return;

    const videos = getAllVideos();
    const videoDuration = 8000; // 8 seconds per video

    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % videos.length);
        setIsTransitioning(false);
      }, 500); // Half second transition
    }, videoDuration);

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentSlide, getAllVideos, isHovered]);

  // Handle video loading
  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  // Handle video loading with timeout fallback
  useEffect(() => {
    if (!videoLoaded) {
      const timeout = setTimeout(() => {
        setVideoLoaded(true);
      }, 3000); // Hide loading overlay after 3 seconds max

      return () => clearTimeout(timeout);
    }
  }, [videoLoaded]);

  // Handle video error
  const handleVideoError = () => {
    console.warn("Video failed to load, falling back to next video");
    setCurrentSlide((prev) => (prev + 1) % getAllVideos().length);
  };

  // Handle window resize for responsive video switching
  useEffect(() => {
    const handleResize = () => {
      // Reset video loaded state when switching between mobile/web
      setVideoLoaded(false);
      // Force video to reload
      if (videoRefs.current[currentSlide]) {
        videoRefs.current[currentSlide].load();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentSlide]);

  const currentSlideData = heroSlides[currentSlide % heroSlides.length];
  const currentVideo = getCurrentVideo();

  const renderDesktopSlide = (slide, videoSrc) => {
    return (
      <div
        className="relative w-full h-full max-w-full overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background Video */}
        <video
          ref={(el) => {
            if (el) videoRefs.current[currentSlide] = el;
          }}
          src={videoSrc}
          autoPlay={!isHovered}
          muted
          loop
          playsInline
          className="object-contain w-full h-full"
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />

        {/* Loading overlay */}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Loading...</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMobileSlide = (slide, videoSrc) => {
    return (
      <div
        className="relative w-full  max-w-full overflow-hidden"
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
      >
        {/* Background Video */}
        <video
          ref={(el) => {
            if (el) videoRefs.current[currentSlide] = el;
          }}
          src={videoSrc}
          autoPlay={!isHovered}
          muted
          loop
          playsInline
          className="object-contain w-full h-full"
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />

      

        {/* Loading overlay */}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
              <p className="text-white text-sm">Loading...</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="relative w-full overflow-hidden pt-14">
      {/* Video Carousel Container */}
      <div className="relative h-full w-full max-w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentSlide}-${
              window.innerWidth < 768 ? "mobile" : "web"
            }`}
            className="h-full w-full max-w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: isTransitioning ? 0.7 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {window.innerWidth < 768 
              ? renderMobileSlide(currentSlideData, currentVideo)
              : renderDesktopSlide(currentSlideData, currentVideo)
            }
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Play/Pause Button */}
      <button
        onClick={() => setIsAutoPlaying(!isAutoPlaying)}
        className="absolute top-6 right-6 z-20 p-3 text-white rounded-full backdrop-blur-sm transition-all duration-300 bg-white/20 hover:bg-white/30"
        aria-label={isAutoPlaying ? "Pause carousel" : "Play carousel"}
      >
        {isAutoPlaying ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        )}
      </button>
    </section>
  );
}
